import React, { useCallback } from 'react';
import clsx from 'clsx';

import Icon from 'carbon-react/lib/components/icon';

import { fontFamily } from '../../../../../ui-styles/ts/base/typography';
import { UITreeNode } from '../types';
import * as styles from './ui-tree-branch.scss';

export interface UITreeBranchProps<T = unknown> extends UITreeNode<T> {
  component: React.FunctionComponent<UITreeNode<T>>;
  expandedBranches?: UITreeNode['id'][];
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onToggleGroup: (id: UITreeNode['id']) => void;
  nested?: boolean;
}

export const UITreeBranch = <T,>(props: UITreeBranchProps<T>) => {
  const { component, expandedBranches = [], id, label, children = [], onChange, onToggleGroup } = props;
  const expanded = expandedBranches.includes(id);

  const handleToggleGroup = useCallback(() => {
    onToggleGroup(id);
  }, [onToggleGroup, id]);

  return (
    <div
      className={styles.container}
      data-testid={`UITreeBranch-${id}`}
      style={props.nested ? { marginLeft: 15 } : { marginLeft: 0 }}
    >
      <button
        className={styles.accordionTrigger}
        onClick={handleToggleGroup}
        aria-controls={`accordion-content-${id}`}
        aria-expanded={expanded ? 'true' : 'false'}
        id={`accordion-control-${id}`}
      >
        <div className={clsx(styles.chevron, { [styles.chevronExpanded]: expanded })}>
          <Icon type="caret_right" />
        </div>
        <strong className={styles.groupLabel} style={{ fontFamily }}>
          {label}
        </strong>
      </button>
      <div
        className={clsx(styles.group, { [styles.hidden]: !expanded })}
        aria-hidden={!expanded ? 'true' : undefined}
        id={`accordion-content-${id}`}
      >
        {children.map(child =>
          child.type === 'group' ? (
            <UITreeBranch
              {...child}
              key={child.id}
              component={component}
              expandedBranches={expandedBranches}
              onChange={onChange}
              onToggleGroup={onToggleGroup}
              nested
            />
          ) : (
            <div key={child.id}>{component(child)}</div>
          )
        )}
      </div>
    </div>
  );
};
